import { Check } from 'lucide-react';

import { CEFR_LEVELS, type CefrLevel, type LevelProgressRow } from '@/core/types';
import { cn } from '@/lib/utils';

type Props = {
  levelProgress: LevelProgressRow[];
  /** Empty array = "all levels" (no filter). */
  selected: CefrLevel[];
  onChange: (next: CefrLevel[]) => void;
};

export function LevelChips({ levelProgress, selected, onChange }: Props) {
  const isAllLevels = selected.length === 0;
  const progressByLevel = new Map(levelProgress.map((p) => [p.level, p]));

  const toggleLevel = (level: CefrLevel) => {
    if (selected.includes(level)) {
      onChange(selected.filter((l) => l !== level));
    } else {
      onChange([...selected, level]);
    }
  };

  return (
    <div className="flex flex-wrap items-stretch gap-2">
      <button
        type="button"
        onClick={() => onChange([])}
        aria-pressed={isAllLevels}
        className={cn(
          'rounded-full border px-4 py-2 text-sm font-medium transition',
          isAllLevels
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-card text-foreground hover:bg-accent',
        )}
      >
        All levels
      </button>

      {CEFR_LEVELS.map((level) => {
        const p = progressByLevel.get(level) ?? { level, total: 0, memorized: 0, learning: 0 };
        const isSelected = selected.includes(level);
        const isDisabled = p.total === 0;
        const isComplete = p.total > 0 && p.memorized === p.total;
        const pct = p.total > 0 ? Math.round((p.memorized / p.total) * 100) : 0;

        return (
          <button
            key={level}
            type="button"
            disabled={isDisabled}
            onClick={() => toggleLevel(level)}
            aria-pressed={isSelected}
            className={cn(
              'flex min-w-16 flex-col items-center rounded-2xl border px-4 py-2 text-sm font-medium transition',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground'
                : isComplete
                  ? 'border-success bg-success/10 text-success hover:bg-success/15'
                  : 'border-border bg-card text-foreground hover:bg-accent',
            )}
          >
            <span className="flex items-center gap-1 leading-none">
              {isComplete && <Check className="size-3.5" />}
              {level}
            </span>
            <span className="mt-0.5 text-xs leading-none opacity-80">
              {isDisabled ? '—' : `${pct}%`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
